import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Tag,
  Inbox,
  Truck,
  KeyRound,
  BellRing,
  ShieldCheck,
  Settings,
  Calculator,
  BarChart3,
  LogOut,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";
import { ADMIN_LOGIN_PATH, LOGO_URL } from "@/lib/constants";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/tracking", label: "Tracking", icon: Truck },
  { to: "/admin/client-portal", label: "Client Portal", icon: KeyRound },
  { to: "/admin/app-notifications", label: "App Alerts", icon: BellRing },
  { to: "/admin/certificates", label: "Certificates", icon: ShieldCheck },
  { to: "/admin/tax-tools", label: "Tax Tools", icon: Calculator },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate(ADMIN_LOGIN_PATH, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--rsi-bg-subtle)] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-[var(--rsi-navy)] text-white flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl overflow-hidden bg-white border border-white/15 p-1">
              <img src={LOGO_URL} alt="" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Admin Panel</div>
              <div className="font-display font-extrabold">R.S. INTERNATIONAL</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-white font-semibold"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-2 text-xs text-white/60 hover:text-white"
            data-testid="admin-view-site"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View live site
          </Link>
          <div className="text-xs text-white/60 truncate">{user?.email}</div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm font-medium"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setOpen(!open)}
            className="h-10 w-10 rounded-lg grid place-items-center bg-gray-100"
            data-testid="admin-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="font-display font-extrabold text-[var(--rsi-navy)]">R.S. Admin</div>
          <div className="w-10" />
        </div>
        <main className="flex-1 p-6 lg:p-10 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
